/**
 * Dynamic Node Parameters Panel
 * 
 * Renders node parameters dynamically based on inferred schemas
 */

import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Sliders, FileText, AlertCircle, ListOrdered, Sparkles, Eye } from 'lucide-react';
import { Node, Edge } from 'reactflow';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { inferSchemaFromDefinition, getNodeProperties } from '@/lib/schema-inference';
import { DynamicPropertyField } from '../properties/DynamicPropertyField';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import { DroppableInput } from './DroppableInput';
import { DroppableTextarea } from './DroppableTextarea';
import { ToolConfigSection } from './ToolConfigSection';
import { FromAIHelpTab } from '@/components/properties/FromAIHelpTab';
import { CodeAgentPanel, type ChatMessage } from './CodeAgentPanel';
import { HtmlTemplateAgentPanel, type HtmlTemplateChatMessage } from './HtmlTemplateAgentPanel';
import { HtmlTemplatePreviewPanel } from './HtmlTemplatePreviewPanel';
import { ALL_NODE_DEFINITIONS } from '@shared/nodes';
import { isLabelUnique, getNodeLabels, getDuplicateLabelMessage } from '@/utils/node-label-utils';
import { getTemplate } from '@shared/nodes/access-integrations/access-financials/templates';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface DynamicNodeParametersPanelProps {
  node: Node;
  onUpdate: (field: string, value: unknown) => void;
  onBatchUpdate?: (updates: Record<string, unknown>) => void;
  nodes: Node[];
  edges: Edge[];
  inputData?: InputData[];  // Upstream node data for expression autocomplete
}

export function DynamicNodeParametersPanel({
  node,
  onUpdate,
  onBatchUpdate,
  nodes,
  edges,
  inputData
}: DynamicNodeParametersPanelProps) {
  const [activeTab, setActiveTab] = useState('parameters');
  const [properties, setProperties] = useState<InferredPropertySchema[]>([]);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  // Persist AI chat conversation across tab switches (clear when node changes)
  const [aiChatMessages, setAiChatMessages] = useState<ChatMessage[]>([]);
  const [htmlTemplateChatMessages, setHtmlTemplateChatMessages] = useState<HtmlTemplateChatMessage[]>([]);
  const [lastNodeId, setLastNodeId] = useState<string>(node.id);
  const { type, data } = node;

  // Clear AI chat when switching to a different node
  useEffect(() => {
    if (node.id !== lastNodeId) {
      setAiChatMessages([]);
      setHtmlTemplateChatMessages([]);
      setLastNodeId(node.id);
    }
  }, [node.id, lastNodeId]);

  // Get existing node labels for uniqueness validation (excluding current node)
  const existingLabels = useMemo(() =>
    getNodeLabels(nodes.filter(n => n.id !== node.id)),
    [nodes, node.id]
  );

  // Handle label change with validation
  const handleLabelChange = useCallback((newLabel: string) => {
    if (!isLabelUnique(newLabel, existingLabels)) {
      setLabelError(getDuplicateLabelMessage(newLabel));
    } else {
      setLabelError(null);
    }
    onUpdate('label', newLabel);
  }, [existingLabels, onUpdate]);
  
  // Check if this node is tool-capable
  const nodeDefinition = type ? ALL_NODE_DEFINITIONS[type] : null;
  const isToolCapableNode = nodeDefinition?.toolCapable?.enabled || false;
  
  // Check if this node is connected to an AI Agent via the tools handle
  const isConnectedToAIAgent = useMemo(() => {
    if (!isToolCapableNode) return false;
    
    // Find edges that connect to this node
    const incomingEdges = edges.filter(edge => edge.target === node.id);
    
    // Check if any edge comes from an AI Agent node's tools handle
    return incomingEdges.some(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      return sourceNode?.type === 'ai-agent' && edge.sourceHandle === 'tools';
    });
  }, [isToolCapableNode, edges, node.id, nodes]);
  
  // Only show Help tab when connected to AI Agent
  const isToolCapable = isToolCapableNode && isConnectedToAIAgent;
  
  // Check if this node has deployment properties (recalculate when properties change)
  const hasDeploymentProperties = useMemo(() => {
    const result = properties.some(p => (p.section || 'properties') === 'deployment');
    // console.log('🔍 hasDeploymentProperties check:', { 
    //   propertiesCount: properties.length, 
    //   result,
    //   deploymentProps: properties.filter(p => (p.section || 'properties') === 'deployment').map(p => p.key),
    //   allSections: properties.map(p => ({ key: p.key, section: p.section }))
    // });
    return result;
  }, [properties]);

  // Load schema on mount or when node type changes
  useEffect(() => {
    if (!type) {
      setSchemaError('Node type is required');
      setProperties([]);
      return;
    }

    const result = inferSchemaFromDefinition(type);
    if (result.success && result.schema) {
      setProperties(result.schema.properties);
      setSchemaError(null);
    } else {
      setSchemaError(result.error || 'Failed to load schema');
      setProperties([]);
    }
  }, [type]);

  // Track which nodes have had defaults applied to prevent infinite loops
  const appliedDefaultsRef = useRef<string | null>(null);

  // Save default values to parameters when they're not yet saved
  // This ensures that displayed defaults are actually persisted for execution
  useEffect(() => {
    if (properties.length === 0) return;

    // Only apply defaults once per node to prevent infinite update loops
    const nodeKey = `${node.id}-${type}`;
    if (appliedDefaultsRef.current === nodeKey) return;

    const parameters = data.parameters || {};
    const defaultsToSave: Record<string, any> = {};

    // Find properties with defaults that aren't yet in parameters
    for (const property of properties) {
      if (property.default !== undefined && parameters[property.key] === undefined) {
        defaultsToSave[property.key] = property.default;
      }
    }

    // If there are defaults to save, update the node
    if (Object.keys(defaultsToSave).length > 0) {
      // Mark this node as having had defaults applied
      appliedDefaultsRef.current = nodeKey;

      // Batch update all defaults at once
      for (const [key, value] of Object.entries(defaultsToSave)) {
        onUpdate(key, value);
      }
    } else {
      // No defaults to save, but mark as processed
      appliedDefaultsRef.current = nodeKey;
    }
  }, [properties, node.id, type, data.parameters, onUpdate]);

  // Track which template selections have been applied to prevent infinite loops
  const appliedTemplateRef = useRef<string | null>(null);

  // Access Financials: Populate query/variables from template when domain/resource/action changes
  useEffect(() => {
    if (type !== 'access-financials') return;

    const parameters = data.parameters || {};
    const queryMode = parameters.queryMode;

    // Only apply templates in template mode
    if (queryMode !== 'template') return;

    // Get domain, resource, action from cascading dropdowns
    const domain = parameters.domain as string;
    const resource = parameters.resource as string;
    const action = parameters.action as string;

    // Need all three to get a template
    if (!domain || !resource || !action) return;

    // Create a unique key for this template selection
    const templateKey = `${node.id}-${domain}|${resource}|${action}`;
    if (appliedTemplateRef.current === templateKey) return;

    // Get the template
    const template = getTemplate(domain, resource, action);
    if (!template) return;

    // Mark as applied to prevent re-application
    appliedTemplateRef.current = templateKey;

    // Populate query and variables from template
    if (onBatchUpdate) {
      onBatchUpdate({
        query: template.query,
        variables: template.variables || '{}',
      });
    } else {
      onUpdate('query', template.query);
      onUpdate('variables', template.variables || '{}');
    }

    console.log('[AccessFinancials] Template applied:', {
      domain,
      resource,
      action,
      templateName: template.name,
    });
  }, [type, data.parameters, node.id, onUpdate, onBatchUpdate]);

  // Check if a property should be displayed based on displayOptions
  const shouldDisplayProperty = (property: InferredPropertySchema): boolean => {
    if (!property.displayOptions?.show) return true;
    
    const parameters = data.parameters || {};
    
    // Debug logging for maxHistoryMessages
    if (property.key === 'maxHistoryMessages') {
      // console.log('🔍 Property visibility check: maxHistoryMessages', {
      //   displayOptions: property.displayOptions,
      //   parameters,
      //   includeHistory: parameters.includeHistory,
      // });
    }
    
    // Check all conditions in displayOptions.show
    for (const [key, conditions] of Object.entries(property.displayOptions.show)) {
      let currentValue = parameters[key];
      
      // If currentValue is undefined, try to get the default value from the property schema
      if (currentValue === undefined) {
        const dependentProperty = properties.find(p => p.key === key);
        if (dependentProperty && dependentProperty.default !== undefined) {
          currentValue = dependentProperty.default;
          if (property.key === 'maxHistoryMessages') {
          }
        }
      }
      
      // If conditions is an array, check if current value is in the array
      if (Array.isArray(conditions)) {
        if (!conditions.includes(currentValue)) {
          if (property.key === 'maxHistoryMessages') {
          }
          return false;
        }
      } else {
        // If it's a single value, check equality
        if (currentValue !== conditions) {
          if (property.key === 'maxHistoryMessages') {
          }
          return false;
        }
      }
    }
    
    if (property.key === 'maxHistoryMessages') {
    }
    
    return true;
  };

  const renderDynamicParameters = (section: 'properties' | 'settings' | 'deployment' = 'properties') => {
    if (schemaError) {
      return (
        <div className="flex items-center gap-2 p-4 border border-destructive/50 rounded-md bg-destructive/10">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">Schema Error</p>
            <p className="text-xs text-muted-foreground">{schemaError}</p>
          </div>
        </div>
      );
    }

    if (properties.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">
          No parameters available for this node type.
        </div>
      );
    }

    const parameters = data.parameters || {};

    // Filter properties based on section and displayOptions
    const visibleProperties = properties
      .filter(shouldDisplayProperty)
      .filter((property) => {
        // If property has no section, it defaults to 'properties'
        const propertySection = property.section || 'properties';
        return propertySection === section;
      })
      .filter((property) => {
        // Hide options and pagination from Parameters tab for http-request nodes
        // These are now rendered in dedicated tabs (Settings and Pagination)
        if (type === 'http-request' && section === 'properties') {
          const hiddenKeys = ['options', 'pagination'];
          return !hiddenKeys.includes(property.key);
        }
        return true;
      });

    if (visibleProperties.length === 0) {
      // Don't show "No settings" message for http-request since we have HTTP Options below
      if (section === 'settings' && type === 'http-request') {
        return null;
      }
      return (
        <div className="text-sm text-muted-foreground">
          No {section} available for this node type.
        </div>
      );
    }

    console.log('🔍 [DynamicNodeParametersPanel] Rendering section:', {
      section,
      nodeId: node.id,
      parametersKeys: Object.keys(parameters),
      apiKeyInParameters: parameters.apiKey,
      fullParameters: parameters
    });
    
    return (
      <div className="space-y-6 min-w-0 overflow-hidden">
        {visibleProperties.map((property, index) => {
          // Use default value if parameter is undefined
          const paramValue = parameters[property.key];
          const displayValue = paramValue !== undefined ? paramValue : property.default;

          return (
            <DynamicPropertyField
              key={`${property.key}-${index}`}
              property={property}
              value={displayValue}
              onChange={(value) => {
                console.log('🔍 [DynamicNodeParametersPanel] onChange called:', {
                  key: property.key,
                  value,
                  nodeId: node.id
                });
                onUpdate(property.key, value);
              }}
              onParameterChange={onUpdate}
              onBatchParameterChange={onBatchUpdate}
              onPropertyChange={(key, value) => {
                console.log('🔍 [DynamicNodeParametersPanel] onPropertyChange called:', {
                  key,
                  value: typeof value === 'string' && value.startsWith('whk_') ? value.substring(0, 10) + '...' : value,
                  nodeId: node.id
                });
                onUpdate(key, value);
              }}
              nodeId={node.id}
              nodes={nodes}
              allParameters={parameters}
              nodeType={type}
              inputData={inputData}
              onOpenAIHelp={isCodeNode ? () => setActiveTab('ai-help') : isHtmlTemplateNode ? () => setActiveTab('template-ai-help') : undefined}
            />
          );
        })}
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>Continue on Fail</Label>
            <p className="text-xs text-muted-foreground">
              Continue workflow execution even if this node fails
            </p>
          </div>
          <Switch
            checked={data.continueOnFail || false}
            onCheckedChange={(checked) => onUpdate('continueOnFail', checked)}
          />
        </div>
        <div>
          <Label>Retry on Fail</Label>
          <Select
            value={String(data.retryOnFail || 0)}
            onValueChange={(v) => onUpdate('retryOnFail', parseInt(v))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No Retry</SelectItem>
              <SelectItem value="1">1 Retry</SelectItem>
              <SelectItem value="2">2 Retries</SelectItem>
              <SelectItem value="3">3 Retries</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Timeout (seconds)</Label>
          <Input
            type="number"
            value={data.parameters?.timeout ?? 120}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              // Only update if the value is a valid number
              if (!isNaN(value)) {
                onUpdate('timeout', Math.max(1, Math.min(600, value)));
              }
            }}
            min={1}
            max={600}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Maximum time for this node to complete (1-600 seconds)
          </p>
        </div>

        {/* HTTP Request specific options */}
        {type === 'http-request' && (
          <>
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-4">HTTP Options</h4>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Follow Redirects</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically follow HTTP 3xx redirects
                </p>
              </div>
              <Switch
                checked={(data.parameters?.followRedirect ?? true) !== false}
                onCheckedChange={(checked) => onUpdate('followRedirect', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Ignore Response Code</Label>
                <p className="text-xs text-muted-foreground">
                  Succeed even if response code is not 2xx
                </p>
              </div>
              <Switch
                checked={data.parameters?.ignoreResponseCode || false}
                onCheckedChange={(checked) => onUpdate('ignoreResponseCode', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Allow Unauthorized Certificates</Label>
                <p className="text-xs text-muted-foreground">
                  Allow self-signed or invalid SSL certificates
                </p>
              </div>
              <Switch
                checked={data.parameters?.allowUnauthorizedCerts || false}
                onCheckedChange={(checked) => onUpdate('allowUnauthorizedCerts', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Include Response Headers and Status</Label>
                <p className="text-xs text-muted-foreground">
                  Return full response with headers and status code
                </p>
              </div>
              <Switch
                checked={data.parameters?.fullResponse || false}
                onCheckedChange={(checked) => onUpdate('fullResponse', checked)}
              />
            </div>
            <div>
              <Label>Response Format</Label>
              <Select
                value={data.parameters?.responseFormat || 'autodetect'}
                onValueChange={(v) => onUpdate('responseFormat', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="autodetect">Autodetect</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="file">File</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                How to parse the response body
              </p>
            </div>
            <div>
              <Label>Proxy</Label>
              <DroppableInput
                value={data.parameters?.proxy || ''}
                onChange={(e) => onUpdate('proxy', e.target.value)}
                placeholder="http://proxy.example.com:8080"
              />
              <p className="text-xs text-muted-foreground mt-1">
                HTTP proxy to use for the request (optional)
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderNotes = () => {
    return (
      <div className="space-y-4">
        <div>
          <Label>Notes</Label>
          <DroppableTextarea
            value={data.notes || ''}
            onChange={(e) => onUpdate('notes', e.target.value)}
            placeholder="Add notes about this node..."
            rows={10}
            className="font-mono text-sm"
          />
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    const params = data.parameters || {};
    const paginationMode = params.paginationMode || 'off';

    return (
      <div className="space-y-4">
        <div>
          <Label>Pagination Mode</Label>
          <Select
            value={paginationMode}
            onValueChange={(v) => onUpdate('paginationMode', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="offset">Offset-based</SelectItem>
              <SelectItem value="page">Page-based</SelectItem>
              <SelectItem value="cursor">Cursor-based</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Automatically paginate through results
          </p>
        </div>

        {paginationMode && paginationMode !== 'off' && (
          <>
            <div>
              <Label>Maximum Requests</Label>
              <DroppableInput
                type="number"
                value={params.maxRequests || 100}
                onChange={(e) => onUpdate('maxRequests', parseInt(e.target.value))}
                min={1}
                max={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum number of paginated requests to make
              </p>
            </div>

            {paginationMode === 'offset' && (
              <>
                <div>
                  <Label>Offset Parameter Name</Label>
                  <DroppableInput
                    value={params.offsetParam || 'offset'}
                    onChange={(e) => onUpdate('offsetParam', e.target.value)}
                    placeholder="offset"
                  />
                </div>
                <div>
                  <Label>Limit Parameter Name</Label>
                  <DroppableInput
                    value={params.limitParam || 'limit'}
                    onChange={(e) => onUpdate('limitParam', e.target.value)}
                    placeholder="limit"
                  />
                </div>
                <div>
                  <Label>Page Size</Label>
                  <DroppableInput
                    type="number"
                    value={params.pageSize || 100}
                    onChange={(e) => onUpdate('pageSize', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
              </>
            )}

            {paginationMode === 'page' && (
              <>
                <div>
                  <Label>Page Parameter Name</Label>
                  <DroppableInput
                    value={params.pageParam || 'page'}
                    onChange={(e) => onUpdate('pageParam', e.target.value)}
                    placeholder="page"
                  />
                </div>
                <div>
                  <Label>Starting Page</Label>
                  <DroppableInput
                    type="number"
                    value={params.startingPage || 1}
                    onChange={(e) => onUpdate('startingPage', parseInt(e.target.value))}
                    min={0}
                  />
                </div>
              </>
            )}

            {paginationMode === 'cursor' && (
              <>
                <div>
                  <Label>Cursor Parameter Name</Label>
                  <DroppableInput
                    value={params.cursorParam || 'cursor'}
                    onChange={(e) => onUpdate('cursorParam', e.target.value)}
                    placeholder="cursor"
                  />
                </div>
                <div>
                  <Label>Cursor Path in Response</Label>
                  <DroppableInput
                    value={params.cursorPath || 'nextCursor'}
                    onChange={(e) => onUpdate('cursorPath', e.target.value)}
                    placeholder="nextCursor or data.pagination.next"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    JSON path to the next cursor value in the response
                  </p>
                </div>
              </>
            )}

            <div>
              <Label>Stop Condition Path</Label>
              <DroppableInput
                value={params.stopConditionPath || ''}
                onChange={(e) => onUpdate('stopConditionPath', e.target.value)}
                placeholder="hasMore or data.length"
              />
              <p className="text-xs text-muted-foreground mt-1">
                JSON path that indicates when to stop (empty array, false, or null stops pagination)
              </p>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-4">Result Handling</h4>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Flatten Results</Label>
                <p className="text-xs text-muted-foreground">
                  Merge all page results into a single array
                </p>
              </div>
              <Switch
                checked={params.flattenResults ?? true}
                onCheckedChange={(checked) => onUpdate('flattenResults', checked)}
              />
            </div>

            {(params.flattenResults ?? true) && (
              <div>
                <Label>Response Data Path</Label>
                <DroppableInput
                  value={params.responseDataPath || 'data'}
                  onChange={(e) => onUpdate('responseDataPath', e.target.value)}
                  placeholder="data"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  JSON path to the array of items in each response (e.g., "data", "results", "items")
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const isHttpRequest = type === 'http-request';
  const isCodeNode = type === 'code';
  const isHtmlTemplateNode = type === 'html-template';

  // Handler for inserting AI-generated code into the correct code parameter based on language
  const handleInsertCode = useCallback((code: string) => {
    const language = data.parameters?.language || 'javascript';
    const parameterKey = language === 'python' ? 'pythonCode' : 'jsCode';
    onUpdate(parameterKey, code);
  }, [onUpdate, data.parameters?.language]);

  // Handler for inserting AI-generated template into the template parameter
  const handleInsertTemplate = useCallback((template: string) => {
    onUpdate('template', template);
  }, [onUpdate]);

  // Full-height tabs that should fill remaining space (not scroll the outer panel)
  const isFullHeightTab = activeTab === 'preview' || activeTab === 'ai-help' || activeTab === 'template-ai-help';

  return (
    <div className="h-full w-full flex flex-col min-h-0">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full min-h-0">
        {/* TabsList is always fixed at top */}
        <div className="px-6 pt-6 flex-shrink-0">
          <TabsList className={`w-full grid mb-0`} style={{ gridTemplateColumns: `repeat(${3 + (hasDeploymentProperties ? 1 : 0) + (isHttpRequest ? 1 : 0) + (isCodeNode ? 1 : 0) + (isHtmlTemplateNode ? 2 : 0) + (isToolCapable ? 1 : 0)}, minmax(0, 1fr))` }}>
            <TabsTrigger value="parameters" className="gap-2">
              <Sliders className="h-4 w-4" />
              Parameters
            </TabsTrigger>
            {hasDeploymentProperties && (
              <TabsTrigger value="deployment" className="gap-2">
                <Settings className="h-4 w-4" />
                Deployment
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
            {isHttpRequest && (
              <TabsTrigger value="pagination" className="gap-2">
                <ListOrdered className="h-4 w-4" />
                Pagination
              </TabsTrigger>
            )}
            {isCodeNode && (
              <TabsTrigger value="ai-help" className="gap-2">
                <Sparkles className="h-4 w-4" />
                AI Help
              </TabsTrigger>
            )}
            {isHtmlTemplateNode && (
              <TabsTrigger value="preview" className="gap-2">
                <Eye className="h-4 w-4" />
                Preview
              </TabsTrigger>
            )}
            {isHtmlTemplateNode && (
              <TabsTrigger value="template-ai-help" className="gap-2">
                <Sparkles className="h-4 w-4" />
                AI Help
              </TabsTrigger>
            )}
            <TabsTrigger value="notes" className="gap-2">
              <FileText className="h-4 w-4" />
              Notes
            </TabsTrigger>
            {isToolCapable && (
              <TabsTrigger value="help" className="gap-2">
                <AlertCircle className="h-4 w-4" />
                Help
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Full-height tabs: fill remaining space directly (no ScrollArea) */}
        {isFullHeightTab ? (
          <div className="flex-1 min-h-0">
            {isCodeNode && (
              <TabsContent value="ai-help" className="h-full m-0 min-w-0 overflow-hidden">
                <CodeAgentPanel
                  language={(data.parameters?.language as 'javascript' | 'python') || 'javascript'}
                  executionTier={(data.parameters?.executionTier as 'sandboxed' | 'extended') || 'sandboxed'}
                  inputData={inputData || []}
                  existingCode={(data.parameters?.language === 'python'
                    ? data.parameters?.pythonCode
                    : data.parameters?.jsCode) as string || ''}
                  onInsertCode={handleInsertCode}
                  externalMessages={aiChatMessages}
                  onMessagesChange={setAiChatMessages}
                />
              </TabsContent>
            )}

            {isHtmlTemplateNode && (
              <TabsContent value="preview" className="h-full m-0 min-w-0 overflow-hidden">
                <HtmlTemplatePreviewPanel
                  template={(data.parameters?.template as string) || ''}
                  primaryColor={(data.parameters?.primaryColor as string) || '#2563eb'}
                  customCss={(data.parameters?.customCss as string) || ''}
                  inputData={inputData || []}
                />
              </TabsContent>
            )}

            {isHtmlTemplateNode && (
              <TabsContent value="template-ai-help" className="h-full m-0 min-w-0 overflow-hidden">
                <HtmlTemplateAgentPanel
                  inputData={inputData || []}
                  existingTemplate={(data.parameters?.template as string) || ''}
                  primaryColor={(data.parameters?.primaryColor as string) || '#2563eb'}
                  currencyCode={(data.parameters?.currencyCode as string) || 'USD'}
                  dateFormat={(data.parameters?.dateFormat as string) || 'yyyy-MM-dd'}
                  outputType={(data.parameters?.outputType as 'html' | 'email') || 'html'}
                  onInsertTemplate={handleInsertTemplate}
                  externalMessages={htmlTemplateChatMessages}
                  onMessagesChange={setHtmlTemplateChatMessages}
                />
              </TabsContent>
            )}
          </div>
        ) : (
          /* Scrollable tabs: wrapped in ScrollArea */
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport className="min-w-0">
              <div className="p-6 min-w-0 overflow-hidden">
                <TabsContent value="parameters" className="space-y-4 min-w-0 overflow-hidden mt-0">
                  <div>
                    <Label>Node Label</Label>
                    <DroppableInput
                      value={data.label}
                      onChange={(e) => handleLabelChange(e.target.value)}
                      placeholder="Enter node label"
                      className={labelError ? 'border-destructive' : ''}
                    />
                    {labelError && (
                      <p className="text-sm text-destructive mt-1">{labelError}</p>
                    )}
                  </div>

                  {/* Tool Configuration Section - shown right after Node Label for tool-capable nodes */}
                  <ToolConfigSection
                    node={node}
                    onUpdate={onUpdate}
                    nodes={nodes}
                    edges={edges}
                  />

                  {renderDynamicParameters()}
                </TabsContent>

                {hasDeploymentProperties && (
                  <TabsContent value="deployment" className="space-y-6 min-w-0 overflow-hidden mt-0">
                    {renderDynamicParameters('deployment')}
                  </TabsContent>
                )}

                <TabsContent value="settings" className="space-y-6 min-w-0 overflow-hidden mt-0">
                  {/* Schema-based settings properties */}
                  {renderDynamicParameters('settings')}

                  {/* Separator if there are schema-based settings */}
                  {properties.some(p => (p.section || 'properties') === 'settings') && (
                    <div className="border-t pt-6" />
                  )}

                  {/* Built-in workflow settings */}
                  {renderSettings()}
                </TabsContent>

                {isHttpRequest && (
                  <TabsContent value="pagination" className="mt-0">
                    {renderPagination()}
                  </TabsContent>
                )}

                <TabsContent value="notes" className="mt-0">
                  {renderNotes()}
                </TabsContent>

                {isToolCapable && (
                  <TabsContent value="help" className="h-full mt-0">
                    <FromAIHelpTab nodeType={type} />
                  </TabsContent>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        )}
      </Tabs>
    </div>
  );
}
